import React, { useMemo, useState } from 'react';
import { ICONS, Icon, TreeNodeModel, TreeSelect } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import styles from './FilterSideBarItemOpen.module.scss';
import { EXCLUDE_SEARCH_FOR_FILTERS } from './constants';

const cx = classNames.bind(styles);

interface FilterProps {
  data: TreeNodeModel[];
  placeholder: string;
  dataTestId?: string;
  onChange: (checkedKeys: string[]) => void;
  checkedKeys: string[];
}

const getFilterKeys = (data: TreeNodeModel): string[] => {
  if (!data.children) {
    return [data.key];
  }
  const childrenTagIds = data.children.flatMap((node) => getFilterKeys(node));
  return [data.key, ...childrenTagIds];
};

const FilterSideBar = ({ data, placeholder, dataTestId, onChange, checkedKeys }: FilterProps): React.ReactElement => {
  const dataKeys = data.flatMap((filter) => getFilterKeys(filter));
  const validSelectedFilterKeys = Array.from(new Set(checkedKeys.filter((key) => dataKeys.includes(key))));
  const [isExpanded, setIsExpanded] = useState(false);

  const handleFilterClick = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleCaretClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    event.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  const renderOption = (placeholder: string) => (
    <div className={cx('filter-placeholder')}>
      <span data-testid="filter-tree-selected-placeholder">{placeholder}</span>
      <span onClick={handleCaretClick}>
        <div className={isExpanded ? cx('expanded') : ''}>
          <Icon name={ICONS.CaretDown} height={8} width={8} />
        </div>
      </span>
    </div>
  );

  const buttonTriggerDisplayValue = useMemo(() => renderOption(placeholder), [validSelectedFilterKeys]);

  const renderFilterName = () => {
    return (
      <div className={cx('filter-name')} data-testid="filter-name">
        {buttonTriggerDisplayValue}
      </div>
    );
  };
  return (
    <div className={cx('body')} data-testid={dataTestId} onClick={handleFilterClick}>
      {renderFilterName()}
      {isExpanded && (
        <div onClick={(event) => event.stopPropagation()}>
          <TreeSelect
            data={data}
            showReset={false}
            showSearch={!EXCLUDE_SEARCH_FOR_FILTERS.includes(placeholder)}
            treeHeight={180}
            checkedKeys={validSelectedFilterKeys}
            setCheckedKeys={(validCheckedKeys) => {
              onChange(validCheckedKeys);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default FilterSideBar;
